import { useMemo } from 'react';
import * as d3 from 'd3';
import { useDimensionsRef } from 'hooks/useDimensionsRef/useDimensionsRef';
import { useChartColors } from 'pages/ReportsPage/components/AnalyticsDashboards/chartColors';
import { useOvertimeDashboardStore } from 'pages/ReportsPage/components/AnalyticsDashboards/OvertimeDashboard/store/overtimeDashboard.store';
import { OvertimeTrendPoint } from 'pages/ReportsPage/components/AnalyticsDashboards/OvertimeDashboard/store/overtimeDashboard.store.types';
import {
  LINE_CHART_MARGIN,
  LINE_CHART_HEIGHT,
} from 'pages/ReportsPage/components/AnalyticsDashboards/OvertimeDashboard/OvertimeDashboard.constants';

interface ParsedTrendPoint {
  date: Date;
  value: number;
  raw: OvertimeTrendPoint;
}

export function useOtTrendChart() {
  const data = useOvertimeDashboardStore((s) => s.overtimeTrend);
  const palette = useChartColors();
  const [containerRef, { width: containerWidth }] = useDimensionsRef<HTMLDivElement>();

  const lineColor = palette.chartColors[0];
  const chartWidth = containerWidth || 600;
  const innerWidth = chartWidth - LINE_CHART_MARGIN.left - LINE_CHART_MARGIN.right;
  const innerHeight = LINE_CHART_HEIGHT - LINE_CHART_MARGIN.top - LINE_CHART_MARGIN.bottom;

  const parsedData = useMemo<ParsedTrendPoint[]>(() => {
    if (!data) return [];
    return data.map((d) => ({
      date: new Date(d.date),
      value: d.overtimeHours,
      raw: d,
    }));
  }, [data]);

  const xScale = useMemo(() => {
    if (parsedData.length === 0) return d3.scaleTime().range([0, innerWidth]);
    const extent = d3.extent(parsedData, (d) => d.date) as [Date, Date];
    return d3.scaleTime().domain(extent).range([0, innerWidth]);
  }, [parsedData, innerWidth]);

  const yScale = useMemo(() => {
    const maxVal = d3.max(parsedData, (d) => d.value) ?? 0;
    return d3
      .scaleLinear()
      .domain([0, maxVal || 10])
      .nice()
      .range([innerHeight, 0]);
  }, [parsedData, innerHeight]);

  const linePath = useMemo(() => {
    const generator = d3
      .line<ParsedTrendPoint>()
      .x((d) => xScale(d.date))
      .y((d) => yScale(d.value));
    return generator(parsedData) ?? '';
  }, [parsedData, xScale, yScale]);

  const xTicks = useMemo(() => {
    const dataCount = parsedData.length;
    const maxTicks = Math.max(2, Math.floor(innerWidth / 80));
    const tickCount = Math.min(dataCount, maxTicks);
    return xScale.ticks(tickCount);
  }, [xScale, parsedData.length, innerWidth]);

  const yTicks = useMemo(() => yScale.ticks(5), [yScale]);

  return {
    data,
    parsedData,
    containerRef,
    containerWidth,
    lineColor,
    chartWidth,
    innerWidth,
    innerHeight,
    xScale,
    yScale,
    linePath,
    xTicks,
    yTicks,
  };
}
